// 里を歩く住人：4人のちびキャラと、仲間の式神（今の進化の姿。種族ごとに1体、最大10体）。
// 絵の土の道の上の道筋を、点から隣の点へ歩いては立ち止まる。押すと名前の吹き出し。
// 里の画面は1分ごと・状態が変わるたびに描き直すので、住人の層は描き直しの外に持ち、見えている絵へ付け替える。
import { CHARS, CHAR_IDS, SHIKIGAMI_IDS, shikiFormName, type Profile } from '@hyakki/core';
import { shikiFormSrc } from '../assets';
import { esc } from './dom';

type Pt = [number, number];
interface Path {
  /** 立ち止まる点（行き先・分かれ道） */
  pts: Pt[];
  /** 隣どうし（点の番号）と、その間の道筋（道の上の中継点。a→b の順） */
  links: [number, number, Pt[]][];
}

/** 道（絵に対する%）：l＝横長の絵（4:3）／p＝縦長の絵（3:4）。art-src/village-v2 の絵の土の道を色で抜き出し、
 *  その上だけを通るよう経路探索で中継点を出した（柵・木・建物を越えない。道場の柵の中には入らない） */
const PATHS: Record<'l' | 'p', Path> = {
  l: {
    pts: [
      [53.2, 49.2], // 0 well-n,
      [43.5, 55.2], // 1 well-w,
      [62.2, 54.3], // 2 well-e,
      [52.5, 63.5], // 3 well-s,
      [23.5, 49.7], // 4 saw,
      [35.9, 27.6], // 5 store,
      [33, 39], // 6 store-road,
      [69.5, 32.8], // 7 shrine,
      [48.1, 30.3], // 8 top,
      [59.5, 41.3], // 9 mid-n,
      [76, 56.8], // 10 granary,
      [91.9, 64.9], // 11 east,
      [29.6, 76.2], // 12 smithy,
      [45.9, 72.3], // 13 smithy-road,
      [51.6, 86.7], // 14 south,
      [57.7, 63.5], // 15 dojo-front
    ],
    links: [
      [0, 1, [[50.9, 49.2], [48.4, 52.5], [45.2, 55.2]]],
      [0, 2, [[55.1, 50.3], [56.7, 50.6], [59.5, 54.3]]],
      [1, 3, [[48.1, 61.2], [50.6, 63.1], [52.1, 63.1]]],
      [2, 3, [[61.9, 54.8], [60.3, 55], [55.7, 61], [54.4, 61]]],
      [1, 6, [[42.5, 54], [39.6, 52.1], [36.3, 51], [35.1, 49.9], [33.6, 49.8], [32.2, 48], [32.2, 44.8], [34.7, 41], [34.7, 40], [34.3, 39.3]]],
      [6, 5, [[35, 39.2], [36.7, 37.1], [37.2, 33.9], [35.7, 30.6]]],
      [6, 4, [[34.1, 39.2], [34.7, 40], [34.7, 41.2], [31.6, 45.5], [28.1, 45.5], [26.1, 48.2], [25.3, 48.3]]],
      [1, 4, [[42.5, 54], [39.6, 52.1], [36.3, 51], [35.1, 49.9], [33.5, 49.8], [30.4, 45.7], [28, 45.7], [26.1, 48.2], [25.3, 48.3]]],
      [5, 8, [[43.2, 28.6], [44.2, 29.7], [46.1, 30.4]]],
      [8, 7, [[49.7, 30.9], [52.7, 33.6], [55.3, 34.3], [58.4, 37.2], [61.8, 37.2], [64.6, 35.2]]],
      [8, 9, [[49.7, 30.9], [52.7, 33.6], [54.7, 34], [57, 35.6], [59.5, 39]]],
      [9, 2, [[59.7, 44.8], [59, 47.1], [59, 50.4]]],
      [7, 9, [[64.4, 35.3], [62.6, 36.6], [60.2, 39.9]]],
      [2, 10, [[68.6, 54.6], [69.5, 55.5], [72.8, 56.8]]],
      [10, 11, [[76.8, 57.8], [79.4, 58.9], [80.8, 60.3], [85.6, 62.1], [87.6, 62.2], [89.4, 64.3]]],
      [3, 13, [[50.8, 65.7], [49.8, 69.2], [47.5, 72.3]]],
      [13, 12, [[45.9, 74.3], [43.4, 77.5], [39.7, 77.5], [36.8, 76.2], [35.2, 74.5], [32.8, 74.6], [31.7, 75], [30.5, 76.2]]],
      [3, 14, [[50.8, 65.7], [49.4, 70.3], [46.7, 73.8], [45.5, 76.2], [45.5, 80.1], [47.2, 82.3], [50.3, 84.3], [51.6, 85.9]]],
      [13, 14, [[45.5, 80.1], [47.2, 82.3], [50.3, 84.3], [51.6, 85.9]]],
      [2, 15, [[61.3, 54.8], [57.3, 60.2], [57.3, 63]]],
      [3, 15, [[53.2, 62.5], [56.3, 62.5], [57.1, 63.5]]]
    ],
  },
  p: {
    pts: [
      [51.6, 42.1], // 0 well-n,
      [39.6, 47.7], // 1 well-w,
      [64.5, 47.7], // 2 well-e,
      [51.6, 55.2], // 3 well-s,
      [21.8, 49.3], // 4 saw,
      [36.4, 23.3], // 5 store,
      [31.4, 30.5], // 6 store-road,
      [67.2, 28.7], // 7 shrine,
      [52, 26.9], // 8 top,
      [66.3, 34.5], // 9 mid-n,
      [71.7, 48.3], // 10 granary,
      [90.8, 56.4], // 11 east,
      [24.9, 71.8], // 12 smithy,
      [45.2, 57.3], // 13 smithy-road,
      [49.7, 96.7], // 14 south,
      [50.1, 13.9], // 15 north
    ],
    links: [
      [0, 1, [[48.7, 42], [46.4, 42.9]]],
      [0, 2, [[55.2, 42.1], [62.6, 47.7]]],
      [1, 3, [[39.6, 50.3], [44.5, 53.9], [47.1, 55.2]]],
      [2, 3, [[63.3, 48.6], [63, 50.1], [58, 53.9], [55.1, 54], [53.3, 55.2]]],
      [1, 6, [[42.5, 45.5], [42.5, 42.8], [40.2, 41.1], [33.3, 37.4], [29.5, 34], [29.4, 32.2]]],
      [6, 5, [[32.3, 28.4], [34.5, 26.7], [36.2, 26.3], [37.6, 25.3], [37.6, 24.3]]],
      [6, 4, [[29.4, 32.2], [29.5, 34], [33.3, 37.4], [40.1, 41], [42.4, 42.7], [42.4, 45.2], [38.4, 48.2], [29.4, 48.3], [28.3, 47.9], [27.1, 48.5]]],
      [1, 4, [[39, 48.2], [29.5, 48.3], [28.3, 47.9], [27.1, 48.5]]],
      [5, 8, [[38.3, 24.8], [43.4, 24.1], [45.6, 25.8], [48.3, 26.4], [50.9, 26.2]]],
      [8, 7, [[55.9, 26.9], [61.5, 29.8], [64.8, 29.8]]],
      [8, 9, [[55.9, 26.9], [59.7, 28.6], [61.2, 29.8], [62.5, 30], [66.3, 33.4]]],
      [9, 2, [[66.3, 36], [65.6, 37.4], [59, 41.7], [59, 44.8], [62.9, 47.7]]],
      [7, 9, [[65.1, 30.1], [65.1, 32.5], [66.3, 33.4]]],
      [2, 10, [[65.6, 48.5], [71.1, 48.7]]],
      [10, 11, [[71.6, 50.2], [73.1, 51.3], [75.1, 51.4], [77.8, 53.2], [80.9, 53.2], [84.3, 54.4], [85.5, 55.3], [87.2, 55.3]]],
      [3, 13, [[49.9, 56.4], [46.4, 56.4]]],
      [13, 12, [[46.3, 57.3], [49.4, 59.5], [49, 67.7], [48.1, 69.1], [42.9, 73.3], [31.2, 73.3], [26.7, 71.8]]],
      [3, 14, [[51.6, 56.4], [50.6, 57.1], [50, 61.8], [49, 63.9], [49, 67.7], [48.1, 69.1], [43.2, 73.2], [43.3, 76.6], [50.4, 82.8], [50.4, 90.6]]],
      [13, 14, [[46.3, 57.3], [49.4, 59.5], [49, 67.7], [48.1, 69.1], [43.2, 73.2], [43.2, 76.5], [50.4, 82.8], [50.4, 90.6]]],
      [8, 15, [[50.8, 26.2], [48.2, 26.4], [45.6, 25.8], [44.1, 24.6], [44.1, 23.2], [48.6, 20.1], [50.7, 18], [50.8, 14.8]]]
    ],
  },
};

/** 歩く速さ（絵の幅に対する %/秒） */
const SPEED = 4.2;

interface Walker {
  el: HTMLElement;
  at: number;
  timer: number;
  /** 道の中での立ち位置のずれ（%）。道幅に収まる小ささで、並んで歩いても重なりにくく */
  dx: number;
  dy: number;
  speed: number;
}

let layer: HTMLElement | null = null;
let mode: 'l' | 'p' = 'l';
let walkers: Walker[] = [];
let key = '';

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
/** 隣の点と、そこまでの道筋（中継点＋行き先） */
const near = (i: number): { to: number; route: Pt[] }[] =>
  PATHS[mode].links.flatMap(([a, b, via]) => (a === i ? [{ to: b, route: [...via, PATHS[mode].pts[b]] }] : b === i ? [{ to: a, route: [...via].reverse().concat([PATHS[mode].pts[a]]) }] : []));
const moveTo = (w: Walker, [x, y]: Pt) => {
  w.el.style.left = `${x + w.dx}%`;
  w.el.style.top = `${y + w.dy}%`;
  w.el.style.zIndex = String(Math.round((y + w.dy) * 10));
};

function step(w: Walker) {
  if (!layer || !w.el.isConnected) return;
  const opts = near(w.at);
  if (!opts.length) return;
  const { to, route } = opts[Math.floor(Math.random() * opts.length)];
  // 絵の縦横比で、縦の%を横の%の長さにそろえる
  const ky = mode === 'l' ? 0.75 : 4 / 3;
  let [px, py] = PATHS[mode].pts[w.at];
  w.el.classList.add('walk');
  const leg = (i: number) => {
    if (!w.el.isConnected) return;
    if (i >= route.length) {
      w.el.classList.remove('walk');
      w.at = to;
      w.timer = window.setTimeout(() => step(w), rnd(1200, 5200));
      return;
    }
    const [x, y] = route[i];
    const sec = Math.max(0.05, Math.hypot(x - px, (y - py) * ky) / (SPEED * w.speed));
    if (Math.abs(x - px) > 0.3) w.el.classList.toggle('flip', x < px);
    w.el.style.transitionDuration = `${sec}s`;
    moveTo(w, route[i]);
    [px, py] = [x, y];
    w.timer = window.setTimeout(() => leg(i + 1), sec * 1000);
  };
  leg(0);
}

function stopAll() {
  for (const w of walkers) clearTimeout(w.timer);
  walkers = [];
}

/** 住人の一覧（ちび4人＋仲間の式神） */
function residents(p: Profile): { id: string; html: string; label: string; cls: string }[] {
  const chars = CHAR_IDS.map((c) => ({ id: c, html: `<img src="/art/chibi-walk/${c}.webp" alt="" draggable="false">`, label: CHARS[c].name, cls: 'vw-char' }));
  const shiki = SHIKIGAMI_IDS.filter((id) => (p.shikigami?.[id]?.star ?? 0) > 0).map((id) => {
    const st = p.shikigami[id];
    return { id, html: `<img src="${shikiFormSrc(id, st.star) ?? ''}" alt="" draggable="false">`, label: shikiFormName(id, st.star), cls: `vw-shiki s${st.star}` };
  });
  return [...chars, ...shiki];
}

/** 里の絵（見えている方）に住人を付ける。描き直しのたびに呼ぶ（住人が変わらなければ歩きを続ける） */
export function attachWalkers(body: HTMLElement, p: Profile) {
  const arts = [...body.querySelectorAll<HTMLElement>('.vil-art')];
  const art = arts.find((a) => a.offsetParent !== null) ?? arts[0];
  if (!art) return;
  const m: 'l' | 'p' = art.classList.contains('port') ? 'p' : 'l';
  const list = residents(p);
  const k = `${m}|${list.map((r) => `${r.id}:${r.cls}`).join(',')}`;
  if (layer && k === key) {
    art.prepend(layer);
    return;
  }
  stopAll();
  key = k;
  mode = m;
  layer = document.createElement('div');
  layer.className = 'vil-walkers';
  layer.innerHTML = list.map((r) => `<button class="vw ${r.cls}" data-vw="${esc(r.id)}" aria-label="${esc(r.label)}"><span class="vw-body">${r.html}</span><span class="vw-shadow"></span><b class="vw-name">${esc(r.label)}</b></button>`).join('');
  art.prepend(layer);
  const n = PATHS[mode].pts.length;
  walkers = [...layer.querySelectorAll<HTMLElement>('.vw')].map((el, i) => {
    const w: Walker = { el, at: (i * 5 + 3) % n, timer: 0, dx: rnd(-0.5, 0.5), dy: rnd(-0.4, 0.4), speed: el.classList.contains('vw-shiki') ? rnd(0.8, 1.3) : rnd(0.85, 1.1) };
    el.style.transitionDuration = '0s';
    moveTo(w, PATHS[mode].pts[w.at]);
    w.timer = window.setTimeout(() => step(w), rnd(300, 3500));
    return w;
  });
  layer.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('.vw');
    if (!b) return;
    e.stopPropagation();
    b.classList.remove('talk');
    void b.offsetWidth;
    b.classList.add('talk', 'hop');
    window.setTimeout(() => b.classList.remove('hop'), 500);
    window.setTimeout(() => b.classList.remove('talk'), 2200);
  });
}

/** 画面を離れるとき */
export function detachWalkers() {
  stopAll();
  layer?.remove();
  layer = null;
  key = '';
}
